import React from "react";
import { useTranslation } from "react-i18next";
import { Controller, Control, FieldErrors } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import styled from "styled-components";
import { DialogInputs } from "../hooks/useMaterials";
import { InfoDot } from "./infoDot";
import useFormUtils from "../utils/formUtils";

const SectionHeader = styled.h4`
  font-weight: 500;
  font-size: 20px;
  letter-spacing: -0.4px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  gap: 0.25rem;

  & .p-inputtext,
  & .p-inputnumber {
    width: 100%;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;

  & > div {
    flex: 1;
    min-width: 200px;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: nowrap;
  justify-content: flex-start;

  > div {
    margin: auto 0;
  }

  > div:first-child {
    height: 0;
    flex: 0 0 280px;
  }

  & > div:last-child {
    flex: 1;
  }
`;

interface BasicProductInfoFieldsProps {
  control: Control<DialogInputs>;
  errors: FieldErrors<DialogInputs>;
  preview?: boolean;
  watchEan?: string;
  watchProductNumber?: string;
  watchResponsibleToReport?: boolean;
  onEanBlur?: () => void;
  onFieldBlur?: () => void;
  onResponsibleToReportUnchecked?: () => void;
}

// Shared "name / ean / product number / responsible to report / comment" fields,
// used by both the product and model forms so their values can be lifted to a parent.
const BasicProductInfoFields: React.FC<BasicProductInfoFieldsProps> = ({
  control,
  errors,
  preview,
  watchEan,
  watchProductNumber,
  watchResponsibleToReport,
  onEanBlur,
  onFieldBlur,
  onResponsibleToReportUnchecked,
}) => {
  const { t } = useTranslation();
  const { getFormErrorMessage } = useFormUtils();

  return (
    <>
      <SectionHeader>{t("header-product-information")}</SectionHeader>

      <InputContainer>
        <label htmlFor="name">{t("label-product-name")}</label>
        <Controller
          name="name"
          control={control}
          rules={{ required: t("label-required") }}
          render={({ field }) => (
            <InputText
              id="name"
              {...field}
              disabled={preview}
              onBlur={onFieldBlur}
            />
          )}
        />
        {getFormErrorMessage(errors, "name")}
      </InputContainer>

      <Row>
        <InputContainer>
          <label htmlFor="ean">{t("label-ean")}</label>
          <Controller
            name="ean"
            control={control}
            rules={{
              required: !watchProductNumber ? t("label-ean-required") : false,
            }}
            render={({ field }) => (
              <InputText
                id="ean"
                {...field}
                disabled={preview}
                onBlur={() => {
                  onEanBlur?.();
                  onFieldBlur?.();
                }}
              />
            )}
          />
          {(errors.ean || errors.productNumber) &&
            getFormErrorMessage(errors, "ean")}
        </InputContainer>

        <InputContainer>
          <InfoDot
            position="right"
            text={t("infotext-product-number")}
            marginLeft="10px"
          >
            <label htmlFor="productNumber">{t("label-product-number")}</label>
          </InfoDot>
          <Controller
            name="productNumber"
            control={control}
            rules={{
              required: !watchEan ? t("label-required") : false,
            }}
            render={({ field }) => (
              <InputText
                id="productNumber"
                {...field}
                disabled={preview}
                onBlur={onFieldBlur}
              />
            )}
          />
        </InputContainer>
      </Row>

      <CheckboxContainer className="responsible-comment-container">
        <InputContainer>
          <Controller
            name="responsibleToReport"
            control={control}
            render={({ field }) => (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Checkbox
                  inputId="responsibleToReport"
                  checked={field.value}
                  onChange={(e) => {
                    field.onChange(e.checked);
                    if (!e.checked) {
                      onResponsibleToReportUnchecked?.();
                    }
                  }}
                  disabled={preview}
                />
                <label htmlFor="responsibleToReport">
                  {t("label-responsible-to-report")}
                </label>
              </div>
            )}
          />
        </InputContainer>

        <InputContainer>
          <label htmlFor="comment">{t("label-comment")}</label>
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <InputText
                id="comment"
                {...field}
                disabled={!watchResponsibleToReport || preview}
                onBlur={onFieldBlur}
              />
            )}
          />
        </InputContainer>
      </CheckboxContainer>
    </>
  );
};

export default BasicProductInfoFields;
